import { useState, useEffect, useCallback, useRef } from 'react'
import { ChromeCastPlugin } from 'features/NativePlugins/Implementations/ChromeCastPlugin/ChromeCastPlugin'
import { ChromecastProps } from './interfaces/ChromecastProps'
import { Chromecast } from './interfaces/Chromecast'

const caster = new ChromeCastPlugin()

const useChromecastNative = ({
  title = '',
  thumbnailUrl = '',
  url,
  currentPlayTime,
  isDvr,
  isLive
}: // eslint-disable-next-line no-undef
ChromecastProps): Chromecast => {
  const [available, setAvailable] = useState(false)
  const [connected, setConnected] = useState(false)
  const [mediaLoaded, setMediaLoaded] = useState(false)
  const [wasConnected, setWasConnected] = useState(false)
  const [wasPlaying, setWasPlaying] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [shouldPlay, setShouldPlay] = useState(false)
  const [canSeek, setCanSeek] = useState(false)
  const [playerState, setPlayerState] = useState<string>()
  const [duration, setDuration] = useState(0)
  const lastPlaybackTime = useRef(0)

  useEffect(() => {
    return () => {
      caster.endCurrentSession()
    }
  }, [])

  useEffect(() => {
    setShouldPlay(false)
  }, [available, url])

  useEffect(() => {}, [shouldPlay])

  useEffect(() => {
    if (connected) setWasConnected(true)
  }, [connected, playerState])

  useEffect(() => {
    const connectStateFn = async () => {
      try {
        setConnected(await caster.isConnected())
      } catch (error) {
        console.error('Error connectStateFn on NativeChromeCastHook : ', error)
      }
    }
    connectStateFn()
  }, [available])

  useEffect(() => {
    const handleSessionChange = (event: { connected: boolean }) => {
      setConnected(event.connected)
      if (!event.connected) {
        setMediaLoaded(false)
        setWasPlaying(false)
      }
    }

    caster.addListener('chromecastSessionChanged', handleSessionChange)
    return () => {
      caster.removeListener('chromecastSessionChanged')
    }
  }, [])

  useEffect(() => {
    if (connected && available && !mediaLoaded) loadMedia()
  }, [connected, available, mediaLoaded, url])

  useEffect(() => {
    if (!connected || !playing) return
    const interval = setInterval(async () => {
      try {
        lastPlaybackTime.current = await caster.getCurrentTime()
      } catch (error) {
        console.error(
          'Error fetching playback time on NativeChromeCastHook :',
          error
        )
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [connected, playing])

  useEffect(() => {
    const handleDurationUpdate = (
      playing: boolean,
      progress: number,
      duration: number
    ) => {
      setDuration(duration)
      setPlaying(playing)
    }

    caster.onDurationUpdate(handleDurationUpdate)

    return () => {
      caster.removeListener('chromecastDurationUpdated')
    }
  }, [])

  useEffect(() => {
    if (connected) caster.setVolume(0.05).catch(console.error)
  }, [connected])

  useEffect(() => {
    const checkAvailability = async () => {
      const result = await caster.checkAvailability()
      setAvailable(result)
    }

    checkAvailability()
  }, [mediaLoaded, connected])

  const loadMedia = useCallback(
    async (src?: string) => {
      if (!available) return
      try {
        await caster.loadMedia(
          src ?? url,
          title,
          thumbnailUrl,
          duration,
          currentPlayTime,
          isLive,
          isDvr
        )
        setMediaLoaded(true)
        setCanSeek(true)
        setShouldPlay(false)
      } catch (error) {
        console.error('Error loading media on NativeChromeCastHook :', error)
      }
    },
    [available, url, currentPlayTime]
  )

  const playPause = useCallback(async () => {
    if (!connected) return
    try {
      await caster.playPause()
      setPlaying((prev) => !prev)
      setPlayerState(playing ? 'PAUSED' : 'PLAYING')
    } catch (error) {
      console.error(
        'Error toggling play/pause on NativeChromeCastHook :',
        error
      )
    }
  }, [connected, playing])

  const seek = useCallback(
    async (time: number) => {
      if (!connected) return
      try {
        await caster.seek(time)
        lastPlaybackTime.current = time
      } catch (error) {
        console.error('Error seeking on NativeChromeCastHook :', error)
      }
    },
    [connected]
  )

  const setVolume = useCallback(
    async (volume: number) => {
      if (!connected) return
      try {
        await caster.setVolume(volume)
      } catch (error) {
        console.error('Error setting volume on NativeChromeCastHook :', error)
      }
    },
    [connected]
  )

  const openContextMenu = async () => {
    try {
      await caster.presentCastDialog()
    } catch (error) {
      console.error('Error presenting context menu:', error)
    }
  }

  return {
    available,
    openContextMenu: openContextMenu,
    loadMedia: loadMedia,
    connected,
    wasConnected,
    playing,
    wasPlaying,
    getCurrentTime: () => lastPlaybackTime.current,
    duration,
    lastPlaybackTime: lastPlaybackTime.current,
    playerState,
    mediaLoaded,
    canSeek,
    setShouldPlay,
    play: playPause,
    pause: playPause,
    seek,
    setVolume
  }
}

export { useChromecastNative }
